import { z } from 'zod';
/**
 * Core modules (ROADMAP §1.4). Bolt-ons (`ai`, `calendar-bridge`, `mealplan-ai`) are
 * added here when they land — nothing else in core enumerates modules.
 */
export const MODULE_IDS = ['calendar', 'lists', 'recipes', 'household'];
export const moduleIdSchema = z.enum(MODULE_IDS);
export * from './calendar/index.js';
export * from './lists/index.js';
export * from './recipes/index.js';
export * from './household/index.js';
/**
 * `checkin` (WS-13) is a Phase 1 content module with its own Yjs doc, adopted after
 * `MODULE_IDS` was written for the four §1.4 core modules. `MODULE_IDS` is a
 * documentation constant with no runtime consumers, so it is left alone rather than
 * broadened under three sibling feature workstreams.
 */
export * from './checkin/index.js';
/**
 * `auth` is identity infrastructure, not a content module — it has no Yjs doc and isn't
 * part of `MODULE_IDS`/`moduleIdSchema` (ROADMAP §1.4 core modules). Its schemas live
 * alongside the others here purely for the shared-package export convention.
 */
export * from './auth/index.js';
// `push` is a bolt-on utility module (no Yjs doc, so it stays out of MODULE_IDS above).
export * from './push/index.js';
/** `digest` (WS-14) is a pure-read module with no Yjs doc of its own — same MODULE_IDS
 * exclusion as `push`/`checkin`'s auth sibling above. */
export * from './digest/index.js';
/**
 * `sharing` (WS-15) is the doc-level permission layer — infrastructure like `auth`, with
 * a Postgres table rather than a Yjs doc, so it stays out of `MODULE_IDS` too.
 */
export * from './sharing/index.js';
/** `apps` (WS-16, NotePad + SketchPad) is a Phase 1 content module adopted after
 * `MODULE_IDS` was written — same exclusion as `checkin`'s sibling comment above. */
export * from './apps/index.js';
/**
 * `ai` (WS-18) is a bolt-on: the relay task contracts + `ai_jobs` queue shapes, shared
 * verbatim by the `relay/` process, the server module and the client hook. No Yjs doc of
 * its own — every AI write lands through an existing module's doc — so, like `push` and
 * `sharing`, it stays out of `MODULE_IDS`.
 */
export * from './ai/index.js';
/** Temporary scratch module for the sync dev page + torture suite (WS-1); not a core module. */
export * from './dev/index.js';
/** `games` (WS-17a, Connect-4) is a Phase 2 content module adopted after `MODULE_IDS` was
 * written — same exclusion as `checkin`/`apps`'s sibling comments above. */
export * from './games/index.js';
/** WS-23's guest play-link REST contracts. Exported from here rather than from
 * `games/index.js` on purpose: they depend on `gameMetaSchema`, and re-exporting them from
 * the barrel they depend on would make the edge a cycle (see `games/guestCore.ts`). */
export * from './games/guest.js';
/** `calendarFeed` (WS-22) is the outbound ICS feed's pure logic + REST contract — no Yjs
 * doc of its own, same exclusion as `push`/`digest` above. */
export * from './calendarFeed/index.js';
/** `trackers` (2026-08-18) — "has anyone fed the dog", as a registry + per-tracker tap log.
 * Adopted long after `MODULE_IDS` was written, same exclusion as `checkin`/`apps`/`games`. */
export * from './trackers/index.js';
/** `workSchedule` (2026-09-08) — Kora's shift-sheet import: the transcription contract the
 * relay's `read-shift-grid` task emits, the house-rules interpreter, and the per-member
 * profile kept in the household doc. No Yjs doc of its own (events land in `calendar`),
 * same `MODULE_IDS` exclusion as `ai`/`digest`. */
export * from './workSchedule/index.js';
/** `mcp` (WF-1.2, docs/MCP-CONNECTOR-PLAN.md) — the per-member Claude connector token's
 * REST contract. No Yjs doc of its own, same `MODULE_IDS` exclusion as `push`/`calendarFeed`. */
export * from './mcp/index.js';
/** `feedback` (2026-11-03) — the "tell Dan" feature-request box. A Postgres table, no Yjs
 * doc, same `MODULE_IDS` exclusion as `mcp`/`push`. */
export * from './feedback/index.js';
//# sourceMappingURL=index.js.map